import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { IColumnSettings, ICustomColumn } from 'app/main/shared/shared/table/components/custom-columns-settings/column-settings';
import { map, Observable } from 'rxjs';
import { DOCUMENT_CONTRACT_CARD_SEARCH_PHRASE_MIN_LENGTH } from '../../../constants/constants';
import { IAuditWithUserInterface } from '../../../global/audit.interface';
import { IPageableResponse } from '../../../global/pagging.dto';
import { IConnectedContracts } from '../contacts/organizations/organizations.dto';
import { IGetDocumentIndexData } from '../documents/documents.service';
import { DocumentTypeCustomBox, IContractData } from '../documents/dto/contract-data.dto';
import { ISectionableRecipient } from '../edit-template/edit-template.dto';
import { UserStatusEnum } from '../users/userslisting/store/user-status.enum';
import { ContractTypeEnum } from './constants/constants';
import { ITodoItemDTO } from './contract-control-interfaces/cc-activity.interface';
import {
    ICostChartResponseDTO, IResponseActiveInactiveGraphDTO, IRevenueChartDTO
} from './contract-control-interfaces/contract-control-charts.dto';
import { DefaultFilter } from './contract-control-interfaces/default-filters.interface';

import { ContractControlAddonSetting } from '../addons/addons.dto';
import { ICostForm, ICostResponse, ITotalCost } from './contract-control-detail-view/cc-cost/cost.dto';
import { IRevenueForm, IRevenueResponse, ITotalRevenue } from './contract-control-detail-view/cc-revenue/revenue.dto';
import {
    Cost,
    IDocument,
    DocumentTypeCustomBoxesData,
    DocumentTypeData,
    GetRelatedContract,
    IContractControlData,
    ICustomBoxesDaum,
    IRevenueDTO,
    ISearchContract,
    User
} from './contract-control.dto';
import { INoteForm } from './modals/contract-control-note-modal/contract-control-note-modal.component';
import { IExportCSVPreview } from './modals/export-csv-modal/IExport-csv-preview';
import { IAuditTrailRequestDTO, IAuditTrailRequestResponseDTO } from './store/dto/audit-trail-request.dto';
import { IContractControlSearchParams, IGetContractControlDataResponse, IPeriodRenewal } from './store/dto/contract-control-view';
import { ICCExportPreview, ICCExportPreviewRequest } from './store/dto/export-preview';
import { IExportContractControlRequest } from './store/dto/export-request';
import { ManagerDTO } from './store/dto/manager-list.dto';
import { IEconomicChartResponse } from './contract-control-economics-graph/contract-control-economics.dto';
import { ContractControlCustomBoxesData, IDocumentTypesDto } from '../documents/dto/document-listing.dto';
import { ICustomBoxFormData } from './contract-control-detail-view/cc-custom-box/cc-custom-box.component';
import { IField } from '../settings/documenttype/document-type-settings-modal/document-type-settings.dto';
import { IOverviewFieldForm } from './contract-control-detail-view/cc-overview/cc-overview.component';

export interface ICCGetManagers{
    groups:ICCGetGroupsManagers [];
    users: ICCGetUsersManagers[];
}

export interface ICCGetGroupsManagers{
    default?: 0 | 1;
    id?: number;
    name?: string;
    predefined?: number;
}

export interface ICCGetUsersManagers{
    email: string;
    id: number;
    name: string;
    phone: null | string;
    profile_image: null | string;
    signature: null | string;
    status: UserStatusEnum;
    tenant_owner: boolean;
}

export interface ICreateContractForm {
    id?: number,
    contract_control_name:string,
    document_type_id: number,
    category_ids?: number | number[],
    organization_id?: number
}

export interface IPeriodAndRenewalSettingsForm {
    action: {
        autoRenewal: boolean;
        lastNoticeDate: boolean;
        expirationDate: boolean;
        activityDate: boolean;
    },
    recipient: string,
    notificationInterval: {
        sameDay: boolean;
        oneDayBefore: boolean;
        oneWeekBefore: boolean;
        customDaysBefore: {
            active: boolean;
            value: string | number;
        },
    },
    document_type_id: number;
    notificationType: {
        sms: boolean;
        email: boolean;
    }
}

export interface IUpdatePeriodForm {
}

export interface ICreateNoteForm extends INoteForm{
}

export interface ICreateTODOForm extends ITodoItemDTO{
}

export interface ILinkContractForm {
    contract_control_id:number;
    related_contract_id:number;
}

export interface IAddMultisectionForm {
    contract_control_id?: number;
    sectionable_id?: number;
    section_type?: SECTION_TYPE;
    name?:string
}

@Injectable({
    providedIn: 'root'
})


export class ContractControlService {

    constructor(private http: HttpClient, private _translocoService: TranslocoService) {
    }

    /**
     * Fetch contracts
     * @param page
     * @param q
     * @returns
     */
    fetchContracts(page: number = 1, q: string = '') {
        return this.http.get<IPageableResponse<IGetContractControlDataResponse>>(`contractcontrols?page=${page}&q=${q}`);
    }



    /**
     * Fetch contracts
     * @returns
     * @param requestParams
     */
    fetchContractsPost(requestParams: IContractControlSearchParams): Observable<IPageableResponse<IContractControlData>> {
        if( !this.checkSearchPhraseMinLength(requestParams.q)){
            return new Observable(observer => {
                observer.error(this._translocoService.translate('Document and Contract Card - search length validation', {minLength: DOCUMENT_CONTRACT_CARD_SEARCH_PHRASE_MIN_LENGTH}));
                observer.complete();
            })
        }


        if (requestParams?.ai_query?.length > 0) {
            return this.http.post<IPageableResponse<IContractControlData>>(`contractcontrols/ai-search`, requestParams);
        }

        return this.http.post<IPageableResponse<IContractControlData>>(`contractcontrols/search`, requestParams).pipe(
            map(response => {
                response.data = response.data.map(contract => this.normalizeContractData(contract));
                return response;
            })
        );
    }


    /**
     * Create contracts
     * @param formData
     * @returns http response
     */
    createContract(formData: IContractData) {
        return this.http.post<IGetContractControlDataResponse>('contractcontrols/contract-card', formData);
    }


    /**
     * Get specific contract
     * @param id
     * @returns
     */
    getContract(id: number) {
        return this.http.get<IContractControlResponse>(`contractcontrols/${id}`).pipe(
            map(response => {
                if (response?.data) {
                    response.data = this.normalizeContractData(response.data as any) as any;
                }
                return response;
            })
        )
    }


    fetchOverviewCustomFields(contract_control_id: number): Observable<IDocumentTypesDto> {
        return this.http.get<IDocumentTypesDto>(`contractcontrols/${contract_control_id}/overview`);
    }


    updateOverviewCustomFieldValue(formData: IOverviewFieldForm) {
        return this.http.post(`contractcontrols/${formData.contract_control_id}/overview`, formData);
    }


    /**
     * Update contract
     * @param formData
     * @returns http response
     */
    updateContract(id: number, column: string, value: string | number): Observable<IContractControlResponse> {
        return this.http.put<IContractControlResponse>(`contractcontrols/${id}`, { [column]: value });
    }


    /**
     * Duplicate contract in contract control
     * @param id
     * @returns respones
     */
    duplicateContract(id: number) {
        return this.http.post<IContractControlData>(`contractcontrols/${id}/dublicate`, {});
    }


    /**
     * Delete contract control - contract
     * @param id
     * @returns response
     */
    deleteContract(id: number) {
        return this.http.delete<{message:string}>(`contractcontrols/${id}`);
    }


    /**
     * Search contract with query params
     * @param q
     * @param type
     * @returns http response
     */
    searchContracts(q: string, type: ContractTypeEnum): Observable<IConnectedContracts[]> {
        return this.http.get<IConnectedContracts[]>(`contractcontrols/searchcontracts?q=${q}&contract_type=${type}`)
    }


    /**
     * Connect contracts
     * @param formData
     */
    connectContract(formData: { id: number, contract_type: number, value:number }) {
        return this.http.post('contractcontrols/connectcontracts', formData)
    }


    /**
     * Update period and renewal
     * @param formData
     */
    updatePeriodandRenewal(formData: IUpdatePeriodForm): Observable<IPeriodRenewal> {
        return this.http.post<IPeriodRenewal>('contractcontrols/periodrenewal', formData);
    }


    
    /**
     * Updates the cost information for a specific contract control.
     *
     * @param formData - The cost data to be updated, including the contract control ID.
     * @returns An Observable emitting the response containing the updated cost information.
     */
    updateCost(formData: ICostForm): Observable<ICostResponse> {
        return this.http.post<ICostResponse>(`contractcontrols/${formData?.contract_control_id}/cost`, formData);
    }


    
    /**
     * Updates the revenue information for a specific contract control.
     *
     * @param formData - The revenue data transfer object containing updated revenue details and the contract control ID.
     * @returns An Observable that emits the updated IRevenueResponse object.
     */
    updateRevenue(formData: IRevenueForm): Observable<IRevenueResponse> {
        return this.http.post<IRevenueResponse>(`contractcontrols/${formData?.contract_control_id}/revenue`, formData);
    }



    deleteRevenue(contractId: number, revenueId: number): Observable<{totals: ITotalRevenue}> {
        return this.http.delete<{totals: ITotalRevenue}>(`contractcontrols/${contractId}/${revenueId}/deleteRevenue`);
    }


    /**
     * Create a notes for contracts
     * @param formData
     * @returns
     */
    addNotes(formData: ICreateNoteForm) {
        return this.http.post<IAddNoteResponse>('notes', formData);
    }


    /**
     * Get all contracts notes
     */
    getNotes(contract_control_id: number) {
        return this.http.get<Array<IGetNotesResponse>>(`notes/${contract_control_id}`);
    }


    /**
     * Update notes in contract control
     * @param formData
     * @returns array
     */
    updateNote(formData: INoteForm) {
        return this.http.put(`notes/${formData?.id}`, formData);
    }


    /**
     * Remove note
     * @param id
     * @returns
     */
    deleteNote(id: number) {
        return this.http.delete(`notes/${id}`);
    }


    /**
     * Create task
     * @param formData
     * @returns
     */
    createTodo(formData: Partial<ITodoItemDTO>): Observable<{audit_message: string}> {
        return this.http.post<{audit_message: string}>('todos', formData);
    }


    /**
     * Get tasks
     * @returns response
     */
    getTodos(contract_control_id: number) {
        return this.http.get<Array<IGetTODOResponse>>(`todos/${contract_control_id}`);
    }


    /**
     * Update task
     * @param formData
     * @returns Observable of updated todo with all relations
     */
    updateTodo(formData: Partial<ITodoItemDTO>): Observable<ITodoItemDTO & {audit_message: string}> {
        return this.http.put<ITodoItemDTO & {audit_message: string}>(`todos/${formData?.id}`, formData);
    }


    /**
     * Delete task behalf on id
     * @param id
     * @returns response
     */
    deleteTodo(id: number) {
        return this.http.delete(`todos/${id}`);
    }


    /**
     * Get related contracts
     * @param contract_control_id
     * @returns response
     */
    relatedContracts(contract_control_id: number) {
        return this.http.get<GetRelatedContract[]>(`contractcontrols/${contract_control_id}/relatedcontracts`);
    }


    /**
     * Link contracts
     * @param formData
     * @returns response
     */
    linkContract(formData: ILinkContractForm) {
        return this.http.post(`contractcontrols/${formData?.contract_control_id}/relatedcontractadd`, formData);
    }


    /**
     * Unlink contracts
     * @param contract_control_id
     * @param related_contract_id
     * @returns response
     */
    unlinkContract(contract_control_id: number, related_contract_id: number) {
        return this.http.delete(`contractcontrols/${contract_control_id}/${related_contract_id}/unlinkrelatedcontract`);
    }


    /**
     * Search contracts
     * @param id
     * @param q
     * @returns response
     */
    searchcontract(id: number, q: string) {
        return this.http.get<ISearchContract[]>(`contractcontrols/${id}/searchcontract?q=${q}`);
    }


    /**
     * Connect contacts and managers with contract control
     * @param formData
     * @returns response
     */
    multisectionadd(formData: IAddMultisectionForm): Observable<{audit_message: string}> {
        return this.http.post<{audit_message: string}>('contractcontrols/multisectionadd', formData);
    }


    /**
     * Get multisection data
     * @param contract_control_id
     * @param section_type
     * @returns response
     */
    multisectionget(contract_control_id: number, section_type: SECTION_TYPE) {
        return this.http.post<IMultisectionGetResponse>(`contractcontrols/${contract_control_id}/multisectionget`, { section_type: section_type });
    }


    /**
     * Remove multisection
     * @param id
     * @returns response
     */
    multisectiondelete(id: number): Observable<{audit_message: string}> {
        return this.http.delete<{audit_message: string}>(`contractcontrols/${id}/multisectiondelete`);
    }




    /**
     * Get Audit trail
     * @returns response
     */
    groupauditTrail(contractControlId: number) {
        return this.http.get(`contractcontrols/${contractControlId}/groupaudittrail`);
    }


    /**
     * Get all details of contract
     * @param contractId
     * @returns response
     */
    contractDetails(contractId: number): Observable<IContractControlData> {
        return this.http.get<IContractControlData>(`contractcontrols/${contractId}/detail`).pipe(
            map(contract => this.normalizeContractData(contract))
        );
    }


    /**
     * Get custom boxes
     * @param documentTypeId
     * @param ContractId
     * @returns response
     */
    contractBoxes(documentTypeId: number, ContractId: number): Observable<IDocumentTypesDto> {
        return this.http.get<IDocumentTypesDto>(`contractcontrols/${documentTypeId}/${ContractId}/boxes`);
    }


    /**
     * Re arrange field order in custom box
     * @param fieldId
     * @param formData
     * @returns response
     */
    reArrangeFieldInCustomBox(fieldId: number, formData) {
        return this.http.post(`documenttypes/${fieldId}/fieldorder`, formData);
    }


  

    /**
     * Updates the value and order of a custom box for a specific contract control.
     *
     * @param formData - The form data containing the field ID, contract control ID, value, and order for the custom box.
     * @returns An Observable emitting the updated custom box data for the contract control.
     */
    updateCustomBoxValue(formData: ICustomBoxFormData): Observable<ContractControlCustomBoxesData> {
        return this.http.post<ContractControlCustomBoxesData>(`contractcontrols/${formData.field_id}/${formData.contract_control_id}/custombox`, {value: formData.value, order: formData.order}).pipe(
            map(response => this.normalizeCustomBox(response as any) as any)
        );
    }


    /**
     * Get Upcoming graph data
     * @param contractId
     * @param filter
     * @returns response
     */
    upcomingEvents(contractId: number, filter: number) {
        return this.http.get<IGetCCUpcomingEventsResponseDTO>(`contractcontrols/${contractId}/upcomingevents?filter=${filter}`);
    }


    /**
     * Get contract index data
     * @param contractId
     * @returns response
     */
    contactControlIndexData(contractId: number): Observable<IContractControlResponse> {
        return this.http.get<IContractControlResponse>(`contractcontrols/${contractId}/index-data`).pipe(
            map(response => {
                if (response?.data) {
                    response.data = this.normalizeContractData(response.data as any) as any;
                }
                return response;
            })
        );
    }


    /**
     * Get Custom Column Settings
     * @returns response
     */
    customColumnSettings() {
        return this.http.get<ICustomColumn>('contractcontrols/custom-columns');
    }


    /**
     * Save Custom Column Setting
     * @param formData
     * @returns response
     */
    saveCustomColumnSettings(formData: { column_settings:  IColumnSettings[], order_settings:  IColumnSettings[]   }) {
        return this.http.post('contractcontrols/custom-columns', formData);
    }

    /**
     * Get all active inactive events
     * @param params
     * @returns response
     */
    getActiveInactiveEvents(params: DefaultFilter = null) {
        return this.http.get<IResponseActiveInactiveGraphDTO>(`contractcontrols/active-inactive-events`, { params: this.buildDefaultFilters(params) });
    }

    /**
     * Get all cost charts data
     * @param params
     * @returns response
     */
    getCostChartsData(params: DefaultFilter = null) {
        return this.http.get<ICostChartResponseDTO>(`contractcontrols/cost-graph`, { params: this.buildDefaultFilters(params) });
    }

    /**
     * Get all revenue charts data
     * @param params
     * @returns response
     */
    getRevenueGraphsData(params: DefaultFilter = null) {
        return this.http.get<IRevenueChartDTO>(`contractcontrols/revenue-graph`, { params: this.buildDefaultFilters(params) });
    }


    /**
     * Get managers
     * @param searchQuery
     * @returns response
     */
    getManagers(searchQuery: string) {
        return this.http.get<ICCGetManagers>(`contractcontrols/manager?q=${searchQuery}`).pipe(
            map(response => {
                const users = response.users;
                const groups = response.groups.map(g => ({ ...g, isGroup: true }));
                return [...users, ...groups];
            })
        );
    }

    exportPreview(params: ICCExportPreviewRequest): Observable<IExportCSVPreview<ICCExportPreview>> {
        return this.http.post<IExportCSVPreview<ICCExportPreview>>('export/preview/contracts', params);
    }

    exportContractControl(exportOptions: IExportContractControlRequest) {
        return this.http.post('export/contracts', exportOptions);
    }

    /**
     * get conversion rates
     * @param currency
     * @param amount
     * @param contract_control_id
     * @param type
     */
    conversionRates(currency: string, amount: number, contract_control_id: number, costId: number, type: string) {
        return this.http.post<IConversionRatesResponse>(`contractcontrols/conversion-rates`, { 'currency': currency, 'amount': amount, 'contract_control_id': contract_control_id, id: costId, 'type': type });
    }

    
    deleteCost(contractId: number, costId: number): Observable<{totals: ITotalCost}> {
        return this.http.delete<{totals: ITotalCost}>(`contractcontrols/${contractId}/${costId}/deleteCost`);
    }

    private buildDefaultFilters(params: DefaultFilter = null) {
        if (params) {
            let httpParams = new HttpParams()
            if (params.document_type) {
                httpParams = httpParams.set('document_type_id', params.document_type);
            }

            if (params.status && params.status != 'all') {
                httpParams = httpParams.set('status', params.status);
            }

            if (params.category_ids) {
                httpParams = httpParams.set('category_ids', params.category_ids.toString());
            }
            return httpParams;
        } else {
            return null;
        }
    }


    /**
     * Saving upcoming graph filter
     * @param formData
     */
    saveUpcomingGraphFilter(formData: {id: number, key: string, value: number}){
        return this.http.post('contractcontrols/settings', formData);
    }


    private checkSearchPhraseMinLength(searchString: string) {
        return searchString?.trim()?.length >= DOCUMENT_CONTRACT_CARD_SEARCH_PHRASE_MIN_LENGTH || !searchString;
    }

    getReminderSettings(id: number): Observable<ContractControlAddonSetting> {  
        return this.http.get<ContractControlAddonSetting>(`reminder-settings/${id}`);  
    } 

    saveReminderSettings(formData: ContractControlAddonSetting): Observable<{message: string}> {  
        return this.http.post<{message: string}>('reminder-settings', formData);  
    }


    getEconomicsChartData(contractId: number): Observable<IEconomicChartResponse> {
        return this.http.get<IEconomicChartResponse>(`contractcontrols/${contractId}/charts-data`);
    }


    getContractProgressBar(contractId: number) {
        return this.http.get(`contractcontrols/${contractId}/progress-bar`);
    }

    /**
     * Normalizes contract data to ensure custom boxes are correctly formatted.
     */
    private normalizeContractData(contract: IContractControlData): IContractControlData {
        if (contract?.custom_boxes_data) {
            contract.custom_boxes_data = contract.custom_boxes_data.map(box => this.normalizeCustomBox(box));
        }
        return contract;
    }

    /**
     * Ensures document_type_custom_boxes_data is always an array.
     */
    private normalizeCustomBox(box: ICustomBoxesDaum): ICustomBoxesDaum {
        if (box?.document_type_custom_boxes_data && !Array.isArray(box.document_type_custom_boxes_data)) {
            box.document_type_custom_boxes_data = [box.document_type_custom_boxes_data];
        }
        return box;
    }
}


export interface IMultisectionGetResponse{
    category_ids?: number | number[];
    contacts: ISectionableRecipient[];
    contract_control_image: string;
    contract_control_name: string;
    contract_no: string;
    contract_type: number;
    cost: Cost;
    created_at: string;
    document_type_id?: number;
    filter_range: null;
    id: number;
    is_active: number;
    organization_id?: number;
    parent_contract: number;
    revenue: IRevenueDTO;
    updated_at: string;
    user_id: number;
    workspace_id: number;
}

export interface IMultisectionContactGetResponse extends  IMultisectionGetResponse{

}

export interface IMultisectionManagersGetResponse extends  IMultisectionGetResponse{
    managers:ManagerDTO[];
}

export interface IMultisectionDocumentsGetResponse extends  IMultisectionGetResponse{
    documents?: IDocument[];
}

export interface IGetTODOResponse{
    contract_control_id: number;
    created_at: string;
    description: string;
    due_date: string;
    id: number;
    status: string;
    updated_at: string;
    user_id: number;
    workspace_id: number;
}

export interface IGetNotesResponse{
    comment: string;
    contract_control_id: number;
    created_at: string;
    document_type_id: number;
    id: number;
    updated_at: string;
    user: User;
    user_id: number;
    workspace_id: number;
}


export enum SECTION_TYPE {
    CONTACT  = 'contact',
    MANAGER = 'manager',
    DOCUMENT ='document',
}

export interface IGetCustomBoxesValueDTO extends IAuditWithUserInterface{
    document_type: string;
    document_type_data: IGetCustomBoxesDocumentTypeData;
    id: number;
    is_default: boolean;
    workspace_id: number;
}

export interface IGetCustomBoxesDocumentTypeData extends DocumentTypeData{
    document_type_custom_boxes:DocumentTypeCustomBox[]
}

export interface IConversionRatesResponse{
    conversion_date: string;
    conversion_rate: number;
    revenue_converted_amount?: number;
    cost_converted_amount?: number;

}

export interface IGetCCUpcomingEventsResponseDTO{
    todoEvents: {label:string}[];
    periodRenewalEvents;
    graphData:IGraphDataUpcomingEvents[];
}


export interface IGetContractById extends  IContractControlData{
    connected_contract:IConnectedContracts[]
}

export interface IContractControlResponse {
    data: IGetContractById;
    audit_message: string;
    totals: ITotals;
}

export interface ITotals {
    costs: ITotalCost
    revenues: ITotalRevenue
}
export interface IGraphDataUpcomingEvents{

}

export interface IUpdateContractControlForm{
    id:number;
}

export interface IAddNoteResponse extends IAuditWithUserInterface{
    comment:string;
    contract_control_id:number;
    document_type_id:number;
    id:number;
    workspace_id:number;
}
